// Generic timed-event scheduler: starts registered run events at seeded
// intervals (120–180 s, one at a time) and drives their start/update/end
// lifecycle. The next event is a rarity-weighted roll among defs whose
// minWave gate the director has reached; rare defs go on a two-pick cooldown
// after firing so they stay special. Deterministic: scheduling rolls come
// from a private mulberry32 stream derived from the run seed — never
// Math.random or wall-clock (GDD Appendix A §A.1).

import { RngStream } from './rng.js';
import type { WorldState } from './state.js';
import { getWaveInfo } from './director.js';

/** Scheduling rarity of a custom event (sim/events/index.ts metadata). */
export type EventRarity = 'common' | 'uncommon' | 'rare';

/** Weighted scheduling: commons fill the calendar, rares stay special. */
export const EVENT_RARITY_WEIGHT: Readonly<Record<EventRarity, number>> = {
  common: 100,
  uncommon: 45,
  rare: 15,
};

/**
 * A schedulable run event (e.g. the space storm). The EventsManager starts at
 * most one at a time: start() on the first tick, update() every tick with a
 * 0..1 progress through the duration, end() on the tick the duration elapses
 * (or immediately when the run ends).
 */
export interface GameEventDef {
  id: string;
  /** Display name for the HUD indicator. */
  name: string;
  /** Event duration in sim ticks (60 Hz). */
  duration: number;
  start(world: WorldState, tick: number): void;
  update(world: WorldState, tick: number, progress: number): void;
  end(world: WorldState, tick: number): void;
}

export interface ActiveEvent {
  def: GameEventDef;
  startTick: number;
  /** 0..1 progress through the event's duration at the last update. */
  progress: number;
}

/** One event every 120–180 s at 60 Hz. */
export const EVENT_MIN_INTERVAL_TICKS = 120 * 60;
export const EVENT_MAX_INTERVAL_TICKS = 180 * 60;

// Private stream salt: keeps scheduling rolls independent of the shared world
// streams (same pattern as the wormhole/bonus salts), so spawn/loot/ai
// determinism is unaffected by event timing.
const EVENTS_STREAM_SALT = 0x51a7e4d1;

/**
 * After a rare event fires it is not re-eligible for this many subsequent
 * picks (spec custom-events-v1 §Scheduler: rares must feel rare).
 */
const RARE_COOLDOWN_PICKS = 2;

// Defs carry their scheduling metadata as optional duck-typed fields (the
// custom-events registry attaches them via Object.assign; plain GameEventDefs
// such as the space storm have neither and stay always-eligible commons).
function rarityOf(def: GameEventDef): EventRarity {
  const r = (def as { rarity?: EventRarity }).rarity;
  return r === 'uncommon' || r === 'rare' ? r : 'common';
}

function minWaveOf(def: GameEventDef): number {
  const m = (def as { minWave?: number }).minWave;
  return typeof m === 'number' ? m : 0;
}

export class EventsManager {
  private readonly defs: GameEventDef[] = [];
  private active: ActiveEvent | null = null;
  private stream: RngStream;
  private nextEventTick = 0;
  /** Number of events started since construction/reset (1-based pick ids). */
  private pickCount = 0;
  /** Rare def id → pick id at which it last fired (cooldown bookkeeping). */
  private readonly lastRarePick = new Map<string, number>();

  constructor(seed = 0) {
    this.stream = new RngStream((seed ^ EVENTS_STREAM_SALT) >>> 0);
    this.nextEventTick = this.rollInterval(0);
  }

  register(event: GameEventDef): void {
    this.defs.push(event);
  }

  /** Reseed scheduling from the run seed; call after world.reset(seed). */
  reset(seed: number): void {
    this.stream = new RngStream((seed ^ EVENTS_STREAM_SALT) >>> 0);
    this.active = null;
    this.nextEventTick = this.rollInterval(0);
    this.pickCount = 0;
    this.lastRarePick.clear();
  }

  isActive(id: string): boolean {
    return this.active?.def.id === id;
  }

  getActiveEvents(): ActiveEvent[] {
    return this.active ? [this.active] : [];
  }

  /** Per-tick update (call once per sim step). */
  update(world: WorldState, tick: number): void {
    // Run over: end any running event and stop scheduling new ones.
    if (world.gameOver) {
      if (this.active) {
        this.active.def.end(world, tick);
        this.active = null;
      }
      return;
    }

    if (this.active) {
      const { def, startTick } = this.active;
      const elapsed = tick - startTick;
      if (elapsed >= def.duration) {
        def.end(world, tick);
        this.active = null;
        this.nextEventTick = this.rollInterval(tick);
        return;
      }
      this.active.progress = elapsed / def.duration;
      def.update(world, tick, this.active.progress);
      return;
    }

    if (tick < this.nextEventTick || this.defs.length === 0) return;
    // Weighted rarity roll among wave-eligible defs (see pickDef). Null means
    // every registered def is wave-gated right now: consume no RNG and retry
    // next tick, so eligibility alone decides when the first event lands.
    const def = this.pickDef(world);
    if (!def) return;
    this.pickCount++;
    if (rarityOf(def) === 'rare') this.lastRarePick.set(def.id, this.pickCount);
    this.active = { def, startTick: tick, progress: 0 };
    def.start(world, tick);
  }

  /**
   * Choose the next event: rarity-weighted roll (EVENT_RARITY_WEIGHT) among
   * defs with minWave <= current wave and no active rare cooldown. The wave
   * comes from getWaveInfo: during an intermission the HUD shows the INCOMING
   * wave, so the current wave is displayWave − 1 (0 before wave 1 starts).
   * If the cooldown filter empties the pool, fall back to the wave-gated pool
   * so the calendar never deadlocks on all-rare registrations.
   */
  private pickDef(world: WorldState): GameEventDef | null {
    const info = getWaveInfo(world);
    const wave = info.phase === 'intermission' ? info.displayWave - 1 : info.displayWave;
    let pool = this.defs.filter((d) => minWaveOf(d) <= wave && !this.onRareCooldown(d));
    if (pool.length === 0) pool = this.defs.filter((d) => minWaveOf(d) <= wave);
    if (pool.length === 0) return null;

    let total = 0;
    for (const d of pool) total += EVENT_RARITY_WEIGHT[rarityOf(d)];
    let roll = this.stream.next() * total;
    for (const d of pool) {
      roll -= EVENT_RARITY_WEIGHT[rarityOf(d)];
      if (roll < 0) return d;
    }
    return pool[pool.length - 1];
  }

  /** Rare defs sit out the RARE_COOLDOWN_PICKS picks after their last firing. */
  private onRareCooldown(def: GameEventDef): boolean {
    if (rarityOf(def) !== 'rare') return false;
    const last = this.lastRarePick.get(def.id);
    if (last === undefined) return false;
    return this.pickCount - last < RARE_COOLDOWN_PICKS;
  }

  /** Seeded interval in [EVENT_MIN_INTERVAL_TICKS, EVENT_MAX_INTERVAL_TICKS]. */
  private rollInterval(fromTick: number): number {
    return (
      fromTick +
      Math.floor(this.stream.next_range(EVENT_MIN_INTERVAL_TICKS, EVENT_MAX_INTERVAL_TICKS + 1))
    );
  }
}
